import React, { useState, useEffect, useRef } from 'react';
import {
    ArrowRight, Shield, Sparkles, Lock, Search, Shuffle, Eye,
    Zap, Check, X, Menu, X as XIcon, Play, Pause,
    HardDrive, Layers, Image, Video, Star, ChevronDown,
    ArrowUpRight, MousePointer, Globe
} from 'lucide-react';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//  DESIGN 3: "PRISM"
//
//  An immersive, interaction-rich homepage with a living
//  background canvas, card-based feature orbit, mouse-reactive
//  spotlight, and a bold split-screen aesthetic. Theatrical
//  reveals with staggered micro-animations.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Outfit:wght@100;200;300;400;500;600&display=swap');

  @keyframes fadeUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes orbit1 { 0%{transform:rotate(0deg) translateX(200px) rotate(0deg)} 100%{transform:rotate(360deg) translateX(200px) rotate(-360deg)} }
  @keyframes orbit2 { 0%{transform:rotate(120deg) translateX(160px) rotate(-120deg)} 100%{transform:rotate(480deg) translateX(160px) rotate(-480deg)} }
  @keyframes orbit3 { 0%{transform:rotate(240deg) translateX(240px) rotate(-240deg)} 100%{transform:rotate(600deg) translateX(240px) rotate(-600deg)} }
  @keyframes pulseRing { 0%{transform:scale(0.8);opacity:0.4} 50%{transform:scale(1.1);opacity:0.1} 100%{transform:scale(0.8);opacity:0.4} }
  @keyframes ripple { 0%{transform:scale(1);opacity:0.3} 100%{transform:scale(4);opacity:0} }
  @keyframes glowPulse { 0%,100%{box-shadow:0 0 30px rgba(196,181,253,0.1)} 50%{box-shadow:0 0 60px rgba(196,181,253,0.2)} }
  @keyframes slideRight { from{width:0%} to{width:100%} }
  @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-100%)} }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes morphBlob {
    0%  {border-radius:42% 58% 70% 30% / 45% 45% 55% 55%}
    25% {border-radius:60% 40% 30% 70% / 60% 30% 70% 40%}
    50% {border-radius:30% 70% 55% 45% / 55% 60% 40% 45%}
    75% {border-radius:55% 45% 65% 35% / 35% 55% 45% 65%}
    100%{border-radius:42% 58% 70% 30% / 45% 45% 55% 55%}
  }
  @keyframes gradShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }

  .prism-text {
    background: linear-gradient(120deg, #C4B5FD 0%, #FDA4AF 25%, #A5F3FC 50%, #FDE68A 75%, #C4B5FD 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradShift 6s linear infinite;
  }
  .prism-glass {
    background: rgba(255,255,255,0.025);
    backdrop-filter: blur(30px) saturate(1.4);
    -webkit-backdrop-filter: blur(30px) saturate(1.4);
    border: 1px solid rgba(255,255,255,0.05);
  }
  .prism-glass-active {
    background: rgba(196,181,253,0.04);
    border: 1px solid rgba(196,181,253,0.15);
  }
  .spotlight {
    background: radial-gradient(600px circle at var(--mx) var(--my), rgba(196,181,253,0.04), transparent 60%);
  }
`;

// â”€â”€â”€ InView hook â”€â”€â”€
const useInView = (threshold = 0.15) => {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return { ref, inView };
};

// â”€â”€â”€ Morphing blob â”€â”€â”€
const MorphBlob: React.FC<{ color: string; size: number; top: string; left: string; delay: string }> = ({ color, size, top, left, delay }) => (
    <div className="absolute opacity-[0.04] pointer-events-none" style={{
        width: size, height: size, top, left,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        animation: `morphBlob 15s ease-in-out infinite ${delay}`,
    }} />
);

// â”€â”€â”€ Interactive feature card â”€â”€â”€
const PrismFeatureCard: React.FC<{
    icon: React.ReactNode; title: string; desc: string; accent: string;
    active: boolean; onClick: () => void;
}> = ({ icon, title, desc, accent, active, onClick }) => (
    <button onClick={onClick}
        className={`text-left rounded-2xl p-6 transition-all duration-500 w-full ${active ? 'prism-glass-active' : 'prism-glass hover:border-white/10'}`}
        style={active ? { boxShadow: `0 0 40px ${accent}10` } : {}}>
        <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: active ? `${accent}15` : 'rgba(255,255,255,0.03)', border: `1px solid ${active ? accent + '25' : 'rgba(255,255,255,0.05)'}`, transition: 'all 0.5s ease' }}>
                {icon}
            </div>
            <div>
                <h3 className={`text-base font-medium mb-1 transition-colors duration-500 ${active ? 'text-white/80' : 'text-white/40'}`} style={{ fontFamily: SANS }}>{title}</h3>
                <p className={`text-sm leading-relaxed transition-colors duration-500 ${active ? 'text-white/30' : 'text-white/15'}`} style={{ fontFamily: SANS, fontWeight: 200 }}>{desc}</p>
            </div>
        </div>
    </button>
);

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
//  MAIN COMPONENT
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

interface HomepageDesign3Props {
    onLogin?: () => void;
}

const HomepageDesign3: React.FC<HomepageDesign3Props> = ({ onLogin }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [heroReady, setHeroReady] = useState(false);
    const [mousePos, setMousePos] = useState({ x: '50%', y: '50%' });
    const [activeFeature, setActiveFeature] = useState(0);
    const [activeFaq, setActiveFaq] = useState<number | null>(null);

    useEffect(() => {
        setTimeout(() => setHeroReady(true), 150);
        const handleMouse = (e: MouseEvent) => {
            setMousePos({ x: e.clientX + 'px', y: e.clientY + 'px' });
        };
        window.addEventListener('mousemove', handleMouse);
        return () => window.removeEventListener('mousemove', handleMouse);
    }, []);

    // Auto-rotate features
    useEffect(() => {
        const timer = setInterval(() => setActiveFeature(p => (p + 1) % 6), 4000);
        return () => clearInterval(timer);
    }, []);

    const handleGetStarted = () => {
        if (onLogin) {
            onLogin();
        } else {
            window.location.href = '/';
        }
    };
    const scrollTo = (id: string) => {
        setMenuOpen(false);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const features = [
        { icon: <Lock size={18} className="text-[#C4B5FD]" />, title: 'Your Media, Your Storage', desc: 'Photos and videos stay in Google Drive or your browser. We only store a small index to remember your selections.', accent: '#C4B5FD', visual: 'A secure vault for your memories.' },
        { icon: <Search size={18} className="text-[#FDA4AF]" />, title: 'AI-Powered Search', desc: 'Natural language search across your entire collection. Find anything instantly.', accent: '#FDA4AF', visual: 'Ask for "sunset by the ocean" and find it.' },
        { icon: <Shuffle size={18} className="text-[#A5F3FC]" />, title: 'Smart Shuffle', desc: 'Rediscover forgotten photos and videos with intelligent randomization.', accent: '#A5F3FC', visual: 'Surface memories you forgot you had.' },
        { icon: <Eye size={18} className="text-[#FDE68A]" />, title: 'Immersive Viewer', desc: 'Distraction-free, full-screen playback with customizable intervals.', accent: '#FDE68A', visual: 'Focus entirely on your content.' },
        { icon: <Shield size={18} className="text-[#6EE7B7]" />, title: 'Privacy by Design', desc: 'Your photos and videos are never uploaded to our servers. Media streams directly from Drive to your device.', accent: '#6EE7B7', visual: 'Zero compromises on privacy.' },
        { icon: <Zap size={18} className="text-[#DDD6FE]" />, title: 'Incremental Sync', desc: 'Only changed files update. Fast and bandwidth-respectful.', accent: '#DDD6FE', visual: 'Efficient by design.' },
    ];

    return (
        <div className="min-h-screen text-white overflow-x-hidden" style={{ background: '#08071A' }}>
            <style>{STYLES}</style>

            {/* Mouse spotlight */}
            <div className="fixed inset-0 pointer-events-none spotlight transition-opacity duration-700 z-[1]"
                style={{ '--mx': mousePos.x, '--my': mousePos.y } as React.CSSProperties} />

            {/* Morphing blobs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
                <MorphBlob color="#C4B5FD" size={800} top="-15%" left="-10%" delay="0s" />
                <MorphBlob color="#FDA4AF" size={600} top="40%" left="60%" delay="-5s" />
                <MorphBlob color="#A5F3FC" size={700} top="70%" left="-20%" delay="-10s" />
            </div>

            {/* â”€â”€â”€ Nav â”€â”€â”€ */}
            <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(8,7,26,0.75)', backdropFilter: 'blur(40px) saturate(1.8)', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/refract-logo.png" alt="Refract" className="w-9 h-9 rounded-lg" />
                        <span className="text-lg font-light" style={{ fontFamily: SERIF }}>Refract</span>
                    </div>
                    <div className="hidden md:flex items-center gap-8">
                        {['Features', 'How It Works', 'Pricing'].map(t => (
                            <button key={t} onClick={() => scrollTo(t.toLowerCase().replace(/\s/g, '-'))}
                                className="text-white/25 hover:text-white/60 transition-colors text-sm" style={{ fontFamily: SANS, fontWeight: 300 }}>{t}</button>
                        ))}
                        <button onClick={handleGetStarted}
                            className="relative px-7 py-2.5 rounded-2xl text-sm font-medium overflow-hidden transition-all duration-300 hover:scale-[1.03]"
                            style={{ fontFamily: SANS }}>
                            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #C4B5FD, #FDA4AF, #A5F3FC)', opacity: 0.9 }} />
                            <span className="relative" style={{ color: '#08071A' }}>Get Started</span>
                        </button>
                    </div>
                    <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white/30">
                        {menuOpen ? <XIcon size={22} /> : <Menu size={22} />}
                    </button>
                </div>
                {menuOpen && (
                    <div className="md:hidden px-6 py-6 space-y-4 border-t border-white/5" style={{ background: 'rgba(8,7,26,0.97)' }}>
                        {['Features', 'How It Works', 'Pricing'].map(t => (
                            <button key={t} onClick={() => scrollTo(t.toLowerCase().replace(/\s/g, '-'))} className="block text-white/30 text-sm w-full text-left" style={{ fontFamily: SANS }}>{t}</button>
                        ))}
                        <button onClick={handleGetStarted} className="w-full py-3 rounded-2xl text-sm" style={{ background: 'linear-gradient(135deg, #C4B5FD, #FDA4AF)', color: '#08071A', fontFamily: SANS }}>Get Started</button>
                    </div>
                )}
            </nav>

            {/* â”€â”€â”€ Hero (Split Screen) â”€â”€â”€ */}
            <section className="relative min-h-screen flex items-center px-6 pt-20">
                <div className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left: Copy */}
                    <div>
                        <h1 style={{ opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.2s' }}>
                            <span className="block text-5xl md:text-7xl font-light leading-[1.05] mb-2" style={{ fontFamily: SERIF, color: 'rgba(255,255,255,0.9)' }}>
                                Every photo
                            </span>
                            <span className="block text-5xl md:text-7xl font-light leading-[1.05] mb-2" style={{ fontFamily: SERIF, color: 'rgba(255,255,255,0.9)' }}>
                                tells a story.
                            </span>
                            <span className="block text-5xl md:text-7xl font-light leading-[1.05] prism-text" style={{ fontFamily: SERIF }}>
                                Flip through them all.
                            </span>
                        </h1>

                        <p className="text-white/20 text-base md:text-lg max-w-md mt-8 mb-10 leading-relaxed"
                            style={{ fontFamily: SANS, fontWeight: 200, opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(20px)', transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.35s' }}>
                            Shuffle through your media library. AI-powered search finds any moment.
                            Your photos never leave your control.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start"
                            style={{ opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(20px)', transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.5s' }}>
                            <button onClick={handleGetStarted}
                                className="group px-10 py-4 rounded-2xl text-base font-medium transition-all duration-300 hover:scale-[1.03] flex items-center gap-3 justify-center"
                                style={{ background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)', color: '#08071A', fontFamily: SANS, boxShadow: '0 8px 40px rgba(196,181,253,0.2)' }}>
                                Start Exploring
                                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                            </button>
                            <button onClick={() => scrollTo('how-it-works')}
                                className="px-10 py-4 rounded-2xl text-base transition-all duration-300 hover:scale-[1.02] prism-glass"
                                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: SANS, fontWeight: 300 }}>
                                Learn More
                            </button>
                        </div>

                        <div className="flex items-center gap-6 mt-10 text-[11px] text-white/12"
                            style={{ fontFamily: SANS, fontWeight: 200, opacity: heroReady ? 1 : 0, transition: 'all 1s ease 0.7s' }}>
                            <span className="flex items-center gap-1.5"><Lock size={11} className="text-[#C4B5FD]/30" /> Local-first</span>
                            <span className="flex items-center gap-1.5"><Shield size={11} className="text-[#FDA4AF]/30" /> No tracking</span>
                            <span className="flex items-center gap-1.5"><Zap size={11} className="text-[#A5F3FC]/30" /> Free tier</span>
                        </div>
                    </div>

                    {/* Right: Visual composition */}
                    <div className="hidden lg:flex items-center justify-center relative"
                        style={{ opacity: heroReady ? 1 : 0, transform: heroReady ? 'scale(1)' : 'scale(0.9)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.4s' }}>
                        {/* Central orb */}
                        <div className="relative w-64 h-64">
                            <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle, rgba(196,181,253,0.08) 0%, transparent 70%)', animation: 'pulseRing 4s ease-in-out infinite' }} />
                            <div className="absolute inset-8 rounded-full prism-glass flex items-center justify-center" style={{ animation: 'glowPulse 3s ease-in-out infinite' }}>
                                <Shuffle size={36} className="text-[#C4B5FD]/40" />
                            </div>
                            {/* Orbiting icons */}
                            <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'orbit1 20s linear infinite' }}>
                                <div className="w-10 h-10 rounded-xl prism-glass flex items-center justify-center"><Image size={16} className="text-[#FDA4AF]/50" /></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'orbit2 25s linear infinite' }}>
                                <div className="w-10 h-10 rounded-xl prism-glass flex items-center justify-center"><Video size={16} className="text-[#A5F3FC]/50" /></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'orbit3 18s linear infinite' }}>
                                <div className="w-10 h-10 rounded-xl prism-glass flex items-center justify-center"><Sparkles size={16} className="text-[#FDE68A]/50" /></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* â”€â”€â”€ Testimonial bar â”€â”€â”€ */}
            <section className="py-12 px-6 relative z-10 border-y border-white/[0.03]">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="flex justify-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-[#FDE68A]/40" fill="rgba(253,230,138,0.4)" />)}
                    </div>
                    <p className="text-white/20 text-base italic" style={{ fontFamily: SERIF, fontWeight: 300 }}>
                        "The best way to rediscover your photo library. Period."
                    </p>
                    <p className="text-white/10 text-xs mt-2 tracking-wider uppercase" style={{ fontFamily: SANS, fontWeight: 200 }}>- Early Adopter</p>
                </div>
            </section>

            {/* â”€â”€â”€ Features (Interactive Split) â”€â”€â”€ */}
            <section id="features" className="py-28 px-6 relative z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="text-[10px] tracking-[.3em] uppercase text-[#C4B5FD]/30 block mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>Features</span>
                        <h2 className="text-4xl md:text-6xl font-light" style={{ fontFamily: SERIF }}>
                            <span className="text-white/80">Powerful.</span>{' '}
                            <span className="prism-text">Private.</span>{' '}
                            <span className="text-white/30">Personal.</span>
                        </h2>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8 items-start">
                        {/* Feature list (left) */}
                        <div className="space-y-3">
                            {features.map((f, i) => (
                                <PrismFeatureCard key={i} icon={f.icon} title={f.title} desc={f.desc} accent={f.accent} active={activeFeature === i} onClick={() => setActiveFeature(i)} />
                            ))}
                        </div>

                        {/* Feature visual (right) */}
                        <div className="sticky top-28">
                            <div className="rounded-3xl prism-glass p-12 min-h-[400px] flex flex-col items-center justify-center text-center transition-all duration-700"
                                style={{ borderColor: features[activeFeature].accent + '15', boxShadow: `0 0 80px ${features[activeFeature].accent}08` }}>
                                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-8 transition-all duration-500"
                                    style={{ background: `linear-gradient(135deg, ${features[activeFeature].accent}15, ${features[activeFeature].accent}05)`, border: `1px solid ${features[activeFeature].accent}20` }}>
                                    {React.cloneElement(features[activeFeature].icon as React.ReactElement<any>, { size: 32 })}
                                </div>
                                <h3 className="text-3xl font-light text-white/70 mb-4 transition-all duration-500" style={{ fontFamily: SERIF }}>
                                    {features[activeFeature].title}
                                </h3>
                                <p className="text-white/20 text-base leading-relaxed max-w-sm transition-all duration-500" style={{ fontFamily: SANS, fontWeight: 200 }}>
                                    {features[activeFeature].visual}
                                </p>
                                {/* Progress dots */}
                                <div className="flex gap-2 mt-10">
                                    {features.map((_, i) => (
                                        <button key={i} onClick={() => setActiveFeature(i)}
                                            className="w-8 h-1 rounded-full transition-all duration-500"
                                            style={{ background: i === activeFeature ? features[activeFeature].accent : 'rgba(255,255,255,0.05)' }} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* â”€â”€â”€ How It Works â”€â”€â”€ */}
            <section id="how-it-works" className="py-28 px-6 relative z-10">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-20">
                        <span className="text-[10px] tracking-[.3em] uppercase text-[#FDA4AF]/30 block mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>How It Works</span>
                        <h2 className="text-4xl md:text-6xl font-light" style={{ fontFamily: SERIF }}>
                            <span className="text-white/80">Simple by design.</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { num: '01', title: 'Add Media', desc: 'Connect Google Drive/Dropbox or upload local files. Local by default, cloud backup optional.', accent: '#C4B5FD', icon: <HardDrive size={20} /> },
                            { num: '02', title: 'AI Analyzes', desc: 'Smart tags and metadata generated through secure APIs.', accent: '#FDA4AF', icon: <Sparkles size={20} /> },
                            { num: '03', title: 'Explore', desc: 'Shuffle, search, filter. Rediscover memories you forgot.', accent: '#A5F3FC', icon: <Shuffle size={20} /> },
                        ].map((step, i) => {
                            const { ref, inView } = useInView();
                            return (
                                <div key={i} ref={ref} className="prism-glass rounded-3xl p-8 text-center transition-all duration-700"
                                    style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${i * 150}ms` }}>
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-6"
                                        style={{ background: `${step.accent}10`, border: `1px solid ${step.accent}15`, color: step.accent + '60' }}>
                                        {step.icon}
                                    </div>
                                    <span className="text-[10px] tracking-[.3em] uppercase mb-3 block" style={{ fontFamily: SANS, fontWeight: 300, color: step.accent + '40' }}>{step.num}</span>
                                    <h3 className="text-xl font-light text-white/70 mb-3" style={{ fontFamily: SERIF }}>{step.title}</h3>
                                    <p className="text-white/20 text-sm leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>{step.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* â”€â”€â”€ Privacy Showcase â”€â”€â”€ */}
            <section className="py-28 px-6 relative z-10">
                <div className="max-w-5xl mx-auto">
                    <div className="rounded-[2rem] prism-glass overflow-hidden">
                        <div className="grid lg:grid-cols-2">
                            {/* Left: visual */}
                            <div className="p-12 md:p-16 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(196,181,253,0.03), rgba(253,164,175,0.02))' }}>
                                <div className="relative">
                                    <div className="w-32 h-32 rounded-full flex items-center justify-center" style={{ background: 'rgba(196,181,253,0.06)', border: '1px solid rgba(196,181,253,0.1)' }}>
                                        <Lock size={40} className="text-[#C4B5FD]/30" />
                                    </div>
                                    <div className="absolute -top-4 -right-4 w-10 h-10 rounded-xl prism-glass flex items-center justify-center" style={{ animation: 'float 4s ease-in-out infinite' }}>
                                        <Shield size={16} className="text-[#6EE7B7]/50" />
                                    </div>
                                    <div className="absolute -bottom-2 -left-6 w-10 h-10 rounded-xl prism-glass flex items-center justify-center" style={{ animation: 'float 5s ease-in-out infinite 1s' }}>
                                        <Eye size={16} className="text-[#FDA4AF]/50" />
                                    </div>
                                </div>
                            </div>
                            {/* Right: copy */}
                            <div className="p-12 md:p-16">
                                <span className="text-[10px] tracking-[.3em] uppercase text-[#6EE7B7]/30 block mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>Privacy</span>
                                <h2 className="text-3xl md:text-4xl font-light text-white/80 mb-6" style={{ fontFamily: SERIF }}>
                                    Your data. Your rules.
                                </h2>
                                <ul className="space-y-5">
                                    {[
                                        { title: 'Local-First Storage', desc: 'Files stay in your browser by default. Optional cloud backup is available for cross-device restore.' },
                                        { title: 'Lightweight Index', desc: 'We store only a small index - file IDs and settings - so your library syncs across sessions.' },
                                        { title: 'Direct Streaming', desc: 'Media is fetched directly from Google Drive to your device using your permission.' },
                                        { title: 'Secure Auth', desc: 'Industry-standard Google OAuth. We never see or store your password.' },
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <Check size={14} className="text-[#C4B5FD]/40 mt-1 flex-shrink-0" />
                                            <div>
                                                <span className="text-white/50 text-sm font-medium" style={{ fontFamily: SANS }}>{item.title}</span>
                                                <span className="text-white/20 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}> - {item.desc}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* â”€â”€â”€ Pricing â”€â”€â”€ */}
            <section id="pricing" className="py-28 px-6 relative z-10">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="text-[10px] tracking-[.3em] uppercase text-[#A5F3FC]/30 block mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>Pricing</span>
                        <h2 className="text-4xl md:text-6xl font-light" style={{ fontFamily: SERIF }}>
                            <span className="text-white/80">One plan for every</span>{' '}
                            <span className="prism-text">collection size</span>
                        </h2>
                        <p className="text-white/15 text-sm mt-4" style={{ fontFamily: SANS, fontWeight: 200 }}>No credit card required. Start free, upgrade anytime.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            {
                                tier: 'Free', price: '$0', period: 'forever', accent: '#C4B5FD',
                                features: ['2,000 Drive items', 'Unlimited local files', 'Random shuffle', 'Basic keyword search'],
                                excluded: ['AI search tags', 'Collections'], cta: 'Get Started', highlighted: false,
                            },
                            {
                                tier: 'Pro', price: '$5.99', period: 'month', yearly: '$57.99/yr', accent: '#C4B5FD', earlyAdopter: 'First 200: $4.99/mo',
                                features: ['50,000 Drive items', 'Unlimited local files', 'AI search tags', 'Collections (up to 50)', 'Cloud backup'],
                                excluded: [], cta: 'Start Pro Trial', highlighted: true,
                            },
                            {
                                tier: 'Power', price: '$19.99', period: 'month', yearly: '$199.99/yr', accent: '#A78BFA',
                                features: ['200,000 Drive items', 'Unlimited local files', 'AI search tags', 'Collections (up to 500)', 'Premium features'],
                                excluded: [], cta: 'Start Power Trial', highlighted: false,
                            },
                        ].map((plan, i) => {
                            const { ref, inView } = useInView();
                            return (
                                <div key={i} ref={ref}
                                    className={`rounded-3xl p-8 transition-all duration-700 ${plan.highlighted ? 'prism-glass-active' : 'prism-glass'}`}
                                    style={{
                                        opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${i * 100}ms`,
                                        ...(plan.highlighted ? { boxShadow: '0 0 80px rgba(196,181,253,0.06)' } : {}),
                                    }}>
                                    {plan.highlighted && (
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-[.15em] uppercase mb-4"
                                            style={{ background: 'rgba(196,181,253,0.1)', color: '#C4B5FD', fontFamily: SANS }}>
                                            <Sparkles size={10} /> Popular
                                        </div>
                                    )}
                                    <h3 className="text-xl font-light text-white/60 mb-1" style={{ fontFamily: SERIF }}>{plan.tier}</h3>
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="text-4xl font-light" style={{ fontFamily: SERIF, color: plan.accent }}>{plan.price}</span>
                                        <span className="text-white/15 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>/{plan.period}</span>
                                    </div>
                                    <div className="mb-6">
                                        {plan.yearly && <p className="text-white/10 text-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>{plan.yearly}</p>}
                                        {(plan as { earlyAdopter?: string }).earlyAdopter && <p className="text-white/20 text-[10px] mt-0.5" style={{ fontFamily: SANS, fontWeight: 200 }}>{(plan as { earlyAdopter?: string }).earlyAdopter}</p>}
                                        {!plan.yearly && !(plan as { earlyAdopter?: string }).earlyAdopter && <div />}
                                    </div>
                                    <ul className="space-y-2.5 mb-8">
                                        {plan.features.map((f, fi) => (
                                            <li key={fi} className="flex items-center gap-2.5 text-white/30 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                                                <Check size={12} style={{ color: plan.accent }} />{f}
                                            </li>
                                        ))}
                                        {plan.excluded?.map((f, fi) => (
                                            <li key={`ex-${fi}`} className="flex items-center gap-2.5 text-white/10 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                                                <X size={12} />{f}
                                            </li>
                                        ))}
                                    </ul>
                                    <button onClick={handleGetStarted}
                                        className="w-full py-3.5 rounded-2xl text-sm transition-all duration-300 hover:scale-[1.02]"
                                        style={plan.highlighted
                                            ? { background: `linear-gradient(135deg, ${plan.accent}, ${plan.accent}80)`, color: '#08071A', fontFamily: SANS, fontWeight: 500 }
                                            : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)', fontFamily: SANS, fontWeight: 300 }}>
                                        {plan.cta}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* â”€â”€â”€ FAQ â”€â”€â”€ */}
            <section className="py-28 px-6 relative z-10">
                <div className="max-w-2xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-white/70" style={{ fontFamily: SERIF }}>Questions & Answers</h2>
                    </div>
                    <div className="space-y-3">
                        {[
                            { q: 'Is my data really private?', a: 'Yes. Files are stored locally in your browser by default. If you enable cloud backup, selected local files are uploaded to secure blob storage for cross-device access. We store only a lightweight index (file IDs and settings) for library sync.' },
                            { q: 'Do I need a Google account?', a: 'Only if you want to connect Google Drive. You can use Refract entirely with local files-no account needed.' },
                            { q: 'What file types are supported?', a: 'All common image formats (JPEG, PNG, WebP, HEIC) and video formats (MP4, MOV, WebM). We\'re always adding more.' },
                            { q: 'Can I cancel anytime?', a: 'Absolutely. No contracts, no penalties. Downgrade to Free at any time and keep your local files.' },
                        ].map((item, i) => (
                            <button key={i} onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                                className="w-full text-left prism-glass rounded-2xl p-6 transition-all duration-300"
                                style={activeFaq === i ? { borderColor: 'rgba(196,181,253,0.15)' } : {}}>
                                <div className="flex items-center justify-between">
                                    <span className="text-white/50 text-sm font-medium" style={{ fontFamily: SANS }}>{item.q}</span>
                                    <ChevronDown size={16} className={`text-white/20 transition-transform duration-300 ${activeFaq === i ? 'rotate-180' : ''}`} />
                                </div>
                                <div className={`overflow-hidden transition-all duration-500 ${activeFaq === i ? 'max-h-40 mt-4' : 'max-h-0'}`}>
                                    <p className="text-white/20 text-sm leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>{item.a}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* â”€â”€â”€ Final CTA â”€â”€â”€ */}
            <section className="py-32 px-6 relative z-10">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-4xl md:text-6xl font-light mb-6" style={{ fontFamily: SERIF }}>
                        <span className="text-white/80">Your memories are</span>
                        <br />
                        <span className="prism-text">waiting to be found.</span>
                    </h2>
                    <p className="text-white/15 text-base mb-10 max-w-md mx-auto" style={{ fontFamily: SANS, fontWeight: 200 }}>
                        Start shuffling through your collection. It only takes a moment.
                    </p>
                    <button onClick={handleGetStarted}
                        className="group px-12 py-4 rounded-2xl text-base font-medium transition-all duration-300 hover:scale-[1.03] inline-flex items-center gap-3"
                        style={{ background: 'linear-gradient(135deg, #C4B5FD, #FDA4AF)', color: '#08071A', fontFamily: SANS, boxShadow: '0 8px 50px rgba(196,181,253,0.2)' }}>
                        Get Started Free
                        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="text-white/8 text-xs mt-8" style={{ fontFamily: SANS, fontWeight: 200 }}>
                        No credit card required · Free tier · Your media, your control
                    </p>
                </div>
            </section>

            {/* â”€â”€â”€ Footer â”€â”€â”€ */}
            <footer className="border-t border-white/[0.03] py-16 px-6 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
                        <div>
                            <div className="flex items-center gap-3 mb-5">
                                <img src="/refract-logo.png" alt="Refract" className="w-8 h-8 rounded-md opacity-30" />
                                <span className="text-white/20 text-sm" style={{ fontFamily: SERIF }}>Refract</span>
                            </div>
                            <p className="text-white/10 text-xs leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>
                                Your private media library.<br />Made for creators, by creators.
                            </p>
                        </div>
                        {[
                            { title: 'Product', links: [{ text: 'Features', id: 'features' }, { text: 'How It Works', id: 'how-it-works' }, { text: 'Pricing', id: 'pricing' }, { text: 'FAQ', href: '/faq' }] },
                            { title: 'Legal', links: [{ text: 'Privacy Policy', href: '/privacy' }, { text: 'Terms of Service', href: '/terms' }] },
                            { title: 'Developers', links: [{ text: 'Developer API', href: '/developers' }, { text: 'Status', href: '/faq#troubleshooting' }] },
                        ].map((col, i) => (
                            <div key={i}>
                                <h4 className="text-white/20 text-[10px] tracking-[.25em] uppercase mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>{col.title}</h4>
                                <ul className="space-y-2.5">
                                    {col.links.map((l, li) => (
                                        <li key={li}>
                                            {'id' in l
                                                ? <button onClick={() => scrollTo((l as any).id)} className="text-white/10 hover:text-white/30 text-sm transition-colors" style={{ fontFamily: SANS, fontWeight: 200 }}>{l.text}</button>
                                                : <a href={(l as any).href} className="text-white/10 hover:text-white/30 text-sm transition-colors" style={{ fontFamily: SANS, fontWeight: 200 }}>{l.text}</a>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-white/[0.03] pt-8 text-center text-white/8 text-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>
                        Â© 2026 Refract. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomepageDesign3;
